import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Maximize2, Minimize2 } from "lucide-react";
import type { QueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TableCell, TableRow } from "@/components/ui/table";
import { queryKeys } from "@/queryKeys";
import { merchantFlowKeys } from "@/components/application/merchant-tasks/api/merchantFlowKeys";
import { useEscapedBuilderMenus } from "@/pages/admin/useEscapedBuilderMenus";
import { useServerVariableMarks } from "@/pages/admin/useServerVariableMarks";
import {
  CONTRACT_KIND_LABELS,
  type ContractDocument,
  type ContractKind,
  type FieldSeed,
  type UpdateContractDocumentRequest,
  kindHasJurisdiction,
} from "@/services/docuseal";

export function nextSavedName(name: string, fallback: string): string {
  return name.trim() || fallback;
}

/** The kind whose variables the builder offers before a template is enabled. */
export const DEFAULT_BUILDER_KIND: ContractKind = "main_contract";

/**
 * The two parties every template has: Merchant (the signer) and Funder (staff prefill
 * and fill, never signing). Authors pick between them but cannot add a party. The first
 * is the default for newly drawn fields, so signatures land on the Merchant.
 */
export const TEMPLATE_PARTIES = ["Merchant", "Funder"];

/**
 * The builder's `roles` for an existing template, or undefined to leave its parties
 * alone (the pickers are then hidden). DocuSeal renames parties positionally on Save,
 * so the template's own order is kept, and a lone party keeps its place (and fields)
 * with the other appended after it. Other party sets are not ours to rename.
 */
export function builderParties(roles: readonly string[] | undefined): string[] | undefined {
  if (!roles) return undefined;
  if ([...roles].sort().join("|") === [...TEMPLATE_PARTIES].sort().join("|")) return [...roles];
  if (roles.length !== 1) return undefined;
  return roles[0] === "Funder" ? ["Funder", "Merchant"] : TEMPLATE_PARTIES;
}

/**
 * Builder Values-tab entries for a kind's seed. Empty for a kind with no seed.
 *
 * The server fills every one of these, so each is read-only on the template. The
 * per-submission lock at send time does not cover a form DocuSeal starts itself
 * (its Resubmit button), which falls back to the template's own flags.
 */
export function docusealBuilderFields(seeds: readonly FieldSeed[]) {
  return seeds.map((seed) => ({
    name: seed.docuseal_field_name,
    title: seed.label,
    readonly: true,
  }));
}

/** Kinds without jurisdiction lanes always sit in the null lane. */
export function laneJurisdiction(kind: ContractKind, jurisdiction: string | null): string | null {
  return kindHasJurisdiction(kind) ? jurisdiction : null;
}

/** What a template has saved: its DocuSeal name and its row's type and on/off state. */
export type SavedConfig = Pick<ContractDocument, "name" | "kind" | "jurisdiction" | "is_active">;

/** What the Configuration card shows. Nothing in it is sent until Save. */
export interface ConfigForm {
  name: string;
  kind: ContractKind;
  jurisdiction: string | null;
  enabled: boolean;
}

/** Whether the form differs from what is saved. */
export function isConfigDirty(saved: SavedConfig, form: ConfigForm): boolean {
  return (
    nextSavedName(form.name, saved.name) !== saved.name ||
    form.kind !== saved.kind ||
    laneJurisdiction(form.kind, form.jurisdiction) !== saved.jurisdiction ||
    form.enabled !== saved.is_active
  );
}

/**
 * Row payload for the type and on/off state, omitting each field unless it changed.
 *
 * Sending ``kind`` re-seeds the template's variables and clears the previous
 * kind's unplaced ones, so its presence in the payload means "the admin changed
 * this". The name is not part of it: the caller sends it with every save, and the
 * server renames the DocuSeal template (the row, and so the Documents list, follows it) when it differs.
 */
export function configSavePayload(
  local: Pick<ContractDocument, "kind" | "jurisdiction" | "is_active">,
  form: Omit<ConfigForm, "name">
): UpdateContractDocumentRequest {
  const payload: UpdateContractDocumentRequest = {};
  if (form.kind !== local.kind) payload.kind = form.kind;
  const lane = laneJurisdiction(form.kind, form.jurisdiction);
  if (lane !== local.jurisdiction) payload.jurisdiction = lane;
  if (form.enabled !== local.is_active) payload.is_active = form.enabled;
  return payload;
}

/**
 * "Main Contract", or "Pre-disclosure · New York" for a disclosure. A disclosure with
 * no jurisdiction is the general one.
 */
export function documentTypeLabel(
  doc: Pick<ContractDocument, "kind" | "jurisdiction">,
  jurisdictionNames: Record<string, string>
): string {
  const kind = CONTRACT_KIND_LABELS[doc.kind];
  if (!kindHasJurisdiction(doc.kind)) return kind;
  return `${kind} · ${doc.jurisdiction ? (jurisdictionNames[doc.jurisdiction] ?? doc.jurisdiction) : "General"}`;
}

/** Whether `documents` already has a default in this kind + jurisdiction lane. */
export function hasDefaultInLane(
  documents: readonly ContractDocument[],
  kind: ContractKind,
  jurisdiction: string | null
): boolean {
  return documents.some(
    (doc) =>
      doc.is_active && doc.is_default && doc.kind === kind && doc.jurisdiction === jurisdiction
  );
}

/** Kinds the merchant flow sends from the org's default; without one the send errors. */
export const REQUIRED_DEFAULT_KINDS: readonly ContractKind[] = ["main_contract", "ach_addendum"];

/** Required kinds with no enabled default document, in display order. */
export function missingRequiredKinds(documents: readonly ContractDocument[]): ContractKind[] {
  return REQUIRED_DEFAULT_KINDS.filter((kind) => !hasDefaultInLane(documents, kind, null));
}

/** Why a template cannot be deleted, or null when it can. */
export function deleteUnavailableReason(enabled: boolean): string | null {
  return enabled ? "This template is enabled. Disable it first." : null;
}

/** Every query a template change affects: the template lists and every deal's contract state. */
export const invalidateDocumentQueries = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.contractAdminDocumentsAll() }),
    queryClient.invalidateQueries({
      predicate: (query) => merchantFlowKeys.isDealEvaluation(query.queryKey),
    }),
    queryClient.invalidateQueries({ queryKey: queryKeys.contractStatusAll() }),
    queryClient.invalidateQueries({ queryKey: queryKeys.contractFormAll() }),
  ]);

export const SkeletonRows: React.FC<{ columns: number }> = ({ columns }) => (
  <>
    {[...Array(3)].map((_, rowIndex) => (
      <TableRow key={rowIndex}>
        {[...Array(columns)].map((_, cellIndex) => (
          <TableCell key={cellIndex}>
            <Skeleton className="h-5 w-full" />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

export const MessageRow: React.FC<{
  columns: number;
  children: React.ReactNode;
  muted?: boolean;
}> = ({ columns, children, muted = true }) => (
  <TableRow>
    <TableCell
      colSpan={columns}
      className={`py-10 text-center ${muted ? "text-muted-foreground" : "text-destructive"}`}
    >
      {children}
    </TableCell>
  </TableRow>
);

/**
 * Hosts the DocuSeal builder in a portal pinned over a placeholder, so the iframe
 * keeps its position while the page scrolls and can go fullscreen without a remount.
 */
export const BuilderViewport: React.FC<{
  children: React.ReactNode;
  /** Names of the server-filled variables, so the builder can mark their placed rows. */
  variableNames?: readonly string[];
  /** Hands the element the builder renders into to the page, which binds its events. */
  onHostChange?: (host: HTMLDivElement | null) => void;
  /** Shown beside the Fullscreen button while fullscreen, where the page's own controls are out of reach. */
  fullscreenActions?: React.ReactNode;
}> = ({ children, variableNames = [], onHostChange, fullscreenActions }) => {
  const [builderHost, setBuilderHostState] = useState<HTMLDivElement | null>(null);
  // Stable, or React would detach and re-attach the ref on every render.
  const setBuilderHost = useCallback(
    (host: HTMLDivElement | null) => {
      setBuilderHostState(host);
      onHostChange?.(host);
    },
    [onHostChange]
  );
  useServerVariableMarks(builderHost, variableNames);
  useEscapedBuilderMenus(builderHost);
  const [fullscreen, setFullscreen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [positioned, setPositioned] = useState(false);
  const [windowedStyle, setWindowedStyle] = useState<React.CSSProperties>({});
  const placeholderRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  useLayoutEffect(() => {
    if (!mounted || fullscreen) return;
    const placeholder = placeholderRef.current;
    if (!placeholder) return;

    const positionPortal = () => {
      const rect = placeholder.getBoundingClientRect();
      setWindowedStyle({
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      });
      setPositioned(true);
    };
    positionPortal();
    const observer = new ResizeObserver(positionPortal);
    observer.observe(placeholder);
    window.addEventListener("resize", positionPortal);
    window.addEventListener("scroll", positionPortal, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", positionPortal);
      window.removeEventListener("scroll", positionPortal, true);
    };
  }, [fullscreen, mounted]);

  useEffect(() => {
    if (!fullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [fullscreen]);

  const portal =
    mounted && positioned
      ? createPortal(
          <div
            className={
              fullscreen
                ? "fixed inset-0 z-[100] bg-background p-2"
                : "fixed z-30 overflow-hidden bg-background"
            }
            style={fullscreen ? undefined : windowedStyle}
          >
            <div
              // Top left, level with the builder's header (6px of padding; fullscreen adds
              // the frame's 8px).
              className={cn(
                "absolute z-10 flex items-center gap-2",
                fullscreen ? "left-3.5 top-3.5" : "left-1.5 top-1.5"
              )}
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="bg-background/95 shadow-sm"
                onClick={() => setFullscreen((current) => !current)}
              >
                {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                {fullscreen ? "Exit fullscreen" : "Fullscreen"}
              </Button>
              {fullscreen && fullscreenActions}
            </div>
            <div ref={setBuilderHost} className="h-full w-full overflow-hidden">
              {children}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div ref={placeholderRef} className="h-[720px] w-full" />
      {portal}
    </>
  );
};
